import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../index';

/**
 * Represents a single attribute edit operation that can be undone/redone.
 */
export interface AttributeEdit {
  /** The layer containing the edited feature */
  layerId: string;
  /** The feature whose attribute was edited */
  featureId: string;
  /** The attribute field that was changed */
  field: string;
  /** The value before the edit */
  oldValue: any;
  /** The value after the edit */
  newValue: any;
  /** Timestamp of the edit */
  timestamp: number;
}

interface AttributeHistoryState {
  /** Stack of past edits (most recent last) */
  past: AttributeEdit[];
  /** Stack of undone edits available for redo (most recent last) */
  future: AttributeEdit[];
  /** Maximum number of edits to keep in history */
  maxHistorySize: number;
}

const initialState: AttributeHistoryState = {
  past: [],
  future: [],
  maxHistorySize: 100,
};

const attributeHistorySlice = createSlice({
  name: 'attributeHistory',
  initialState,
  reducers: {
    /**
     * Record a new attribute edit. Clears the redo stack since a new edit
     * invalidates any previously undone operations.
     */
    recordEdit: (state, action: PayloadAction<AttributeEdit>) => {
      state.past.push(action.payload);
      // Trim history if it exceeds max size
      if (state.past.length > state.maxHistorySize) {
        state.past = state.past.slice(state.past.length - state.maxHistorySize);
      }
      // Clear redo stack on new edit
      state.future = [];
    },

    /**
     * Undo the most recent edit. Moves it from past to future stack.
     * The actual attribute value revert is handled by the component/thunk
     * that dispatches this action.
     */
    undo: (state) => {
      const edit = state.past.pop();
      if (edit) {
        state.future.push(edit);
      }
    },

    /**
     * Redo the most recently undone edit. Moves it from future to past stack.
     * The actual attribute value application is handled by the component/thunk
     * that dispatches this action.
     */
    redo: (state) => {
      const edit = state.future.pop();
      if (edit) {
        state.past.push(edit);
      }
    },

    /**
     * Clear all history (e.g., when switching layers or resetting state).
     */
    clearHistory: (state) => {
      state.past = [];
      state.future = [];
    },
  },
});

export const { recordEdit, undo, redo, clearHistory } = attributeHistorySlice.actions;

// Selectors
export const selectCanUndo = (state: RootState) => state.attributeHistory.past.length > 0;
export const selectCanRedo = (state: RootState) => state.attributeHistory.future.length > 0;
export const selectUndoCount = (state: RootState) => state.attributeHistory.past.length;
export const selectRedoCount = (state: RootState) => state.attributeHistory.future.length;
export const selectLastEdit = (state: RootState) => {
  const past = state.attributeHistory.past;
  return past.length > 0 ? past[past.length - 1] : null;
};
export const selectNextRedo = (state: RootState) => {
  const future = state.attributeHistory.future;
  return future.length > 0 ? future[future.length - 1] : null;
};

export default attributeHistorySlice.reducer;
